import React, { useState } from 'react';
import { useModal, openModal, closeModal } from '../../hooks/useModal';
import { PRODUCTS, DAANA_TIERS, DAANA_USES } from '../../data/products';
import { openPayment } from '../../services/payment';

function Daana({ onClose }) {
  const [tier, setTier] = useState(1);
  const [custom, setCustom] = useState("");
  const [use, setUse] = useState("manuscripts");
  const [name, setName] = useState("");
  const [anon, setAnon] = useState(false);
  const [step, setStep] = useState("choose");
  const [processing, setProcessing] = useState(false);

  const amount = custom ? parseInt(custom, 10) || 0 : DAANA_TIERS[tier].v;

  const handleOffer = async () => {
    setProcessing(true);
    try {
      await openPayment({
        amount,
        type: 'donation',
        metadata: {
          use_category: use,
          donor_name: anon ? 'anonymous' : name,
          is_anonymous: anon,
        },
        onSuccess: () => setStep("confirm"),
        onFailure: (err) => {
          console.error('Payment failed:', err);
          setProcessing(false);
        },
        onClose: () => setProcessing(false),
      });
    } catch (err) {
      console.error('Payment error:', err);
      setProcessing(false);
    }
  };

  return (
    <div className="ex-modal" role="dialog" aria-label="Daana — give">
      <button className="ex-close" onClick={onClose}>close ×</button>
      <header className="ex-head">
        <div className="ex-deva">दान</div>
        <div className="ex-title">Daana · the practice of giving</div>
        <p className="ex-sub">In Sanatana Dharma, giving is not charity. It is rehearsal — for the day when you must give away the thing you are most attached to. Begin small.</p>
      </header>

      {step === "choose" && (
        <div className="ex-body">
          <div className="ex-section-label">Choose your sankalpa</div>
          <div className="tier-grid">
            {DAANA_TIERS.map((t, i) => (
              <button key={t.v} className={`tier ${i === tier && !custom ? "on" : ""}`} onClick={() => { setTier(i); setCustom(""); }}>
                <div className="tier-amt">₹{t.v.toLocaleString("en-IN")}</div>
                <div className="tier-label">{t.label}</div>
                <div className="tier-desc">{t.desc}</div>
              </button>
            ))}
          </div>
          <div className="custom-row">
            <span className="custom-label">or a custom offering</span>
            <span className="custom-input">
              <span className="rs">₹</span>
              <input type="number" placeholder="amount" value={custom} onChange={e => setCustom(e.target.value)} />
            </span>
          </div>

          <div className="ex-section-label" style={{ marginTop: 32 }}>Where it should go</div>
          <div className="use-list">
            {DAANA_USES.map(u => (
              <label key={u.k} className={`use-row ${use === u.k ? "on" : ""}`}>
                <input type="radio" name="use" checked={use === u.k} onChange={() => setUse(u.k)} />
                <span>
                  <span className="use-title">{u.t}</span>
                  <span className="use-desc">{u.d}</span>
                </span>
              </label>
            ))}
          </div>

          <div className="ex-section-label" style={{ marginTop: 32 }}>In whose name</div>
          <div className="name-row">
            <input type="text" placeholder="your name (or someone you'd like to honour)" value={name} disabled={anon} onChange={e => setName(e.target.value)} />
            <label className="anon-check">
              <input type="checkbox" checked={anon} onChange={e => setAnon(e.target.checked)} />
              <span>give anonymously</span>
            </label>
          </div>

          <button className="ex-cta" disabled={amount < 1 || processing} onClick={handleOffer}>
            {processing ? "Opening payment..." : `offer ₹${amount.toLocaleString("en-IN")} →`}
          </button>
          <p className="ex-fineprint">UPI · Card · Net Banking. Receipt issued.</p>
        </div>
      )}

      {step === "confirm" && (
        <div className="ex-body center">
          <div className="confirm-deva">ॐ</div>
          <div className="confirm-line">Thank you, {anon || !name ? "friend" : name}.</div>
          <p className="confirm-prose">
            Your offering of ₹{amount.toLocaleString("en-IN")} toward{" "}
            <i>{DAANA_USES.find(u => u.k === use).t.toLowerCase()}</i>{" "}
            has been received. A lamp has been lit for you on the wall of remembrance.
          </p>
          <div className="confirm-actions">
            <button className="ex-cta-sec" onClick={() => { setStep("choose"); setName(""); setCustom(""); }}>give again</button>
            <button className="ex-cta" onClick={onClose}>return to the story</button>
          </div>
        </div>
      )}
    </div>
  );
}

function GranthBhandar({ onClose }) {
  const [cart, setCart] = useState([]);
  const [checkingOut, setCheckingOut] = useState(false);
  const add = (p) => setCart(c => c.find(x => x.id === p.id) ? c.map(x => x.id === p.id ? { ...x, q: x.q + 1 } : x) : [...c, { ...p, q: 1 }]);
  const remove = (id) => setCart(c => c.filter(x => x.id !== id));
  const total = cart.reduce((s, x) => s + x.price * x.q, 0);

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    setCheckingOut(true);
    try {
      await openPayment({
        amount: total,
        type: 'shop',
        metadata: {
          items: cart.map(item => ({ id: item.id, quantity: item.q, price: item.price })),
        },
        onSuccess: () => {
          setCart([]);
          alert('Order placed successfully! You will receive confirmation shortly.');
        },
        onFailure: () => setCheckingOut(false),
        onClose: () => setCheckingOut(false),
      });
    } catch (err) {
      console.error('Checkout error:', err);
      setCheckingOut(false);
    }
  };

  return (
    <div className="ex-modal" role="dialog" aria-label="Granth Bhandar">
      <button className="ex-close" onClick={onClose}>close ×</button>
      <header className="ex-head">
        <div className="ex-deva">ग्रन्थभण्डार</div>
        <div className="ex-title">Granth Bhandar · the book-house</div>
        <p className="ex-sub">Bring a granth home. Every order supports the manuscript-preservation programme.</p>
      </header>

      <div className="shop-layout">
        <div className="shop-grid">
          {PRODUCTS.map(p => (
            <article key={p.id} className="prod">
              <div className="prod-leaf">
                <div className="prod-deva">{p.deva}</div>
              </div>
              <div className="prod-body">
                <div className="prod-title">{p.title}</div>
                <div className="prod-sub">{p.sub}</div>
                <div className="prod-meta"><span>{p.kind}</span><span className="dot">·</span><span>{p.stock}</span></div>
                <div className="prod-foot">
                  <span className="prod-price">₹{p.price.toLocaleString("en-IN")}</span>
                  <button className="prod-add" onClick={() => add(p)}>add to cart</button>
                </div>
              </div>
            </article>
          ))}
        </div>

        <aside className="cart-side">
          <div className="cart-head">
            <div className="cart-title">Your offering</div>
            <div className="cart-count">{cart.reduce((s, x) => s + x.q, 0)} items</div>
          </div>
          {cart.length === 0 ? (
            <p className="cart-empty">No granths chosen yet. The shelves wait.</p>
          ) : (
            <>
              <ul className="cart-list">
                {cart.map(x => (
                  <li key={x.id}>
                    <span className="ci-title">{x.title}</span>
                    <span className="ci-row">
                      <span>{x.q} × ₹{x.price.toLocaleString("en-IN")}</span>
                      <button className="ci-rm" onClick={() => remove(x.id)}>remove</button>
                    </span>
                  </li>
                ))}
              </ul>
              <div className="cart-total">
                <span>total</span>
                <span>₹{total.toLocaleString("en-IN")}</span>
              </div>
              <button className="ex-cta" disabled={checkingOut} onClick={handleCheckout}>
                {checkingOut ? "Processing..." : "checkout · UPI / Card"}
              </button>
              <p className="ex-fineprint">Free shipping above ₹999.</p>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

function DiyaWall({ onClose }) {
  const KEY = "sanatana_diyas";
  const [diyas, setDiyas] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch (e) { return []; }
  });
  const [name, setName] = useState("");
  const [wish, setWish] = useState("");
  const light = () => {
    if (!wish.trim()) return;
    const next = [...diyas, { id: Date.now(), name: name.trim() || "anonymous", wish: wish.trim(), at: new Date().toISOString() }];
    setDiyas(next);
    try { localStorage.setItem(KEY, JSON.stringify(next)); } catch (e) {}
    setName(""); setWish("");
  };

  return (
    <div className="ex-modal" role="dialog" aria-label="Diya wall">
      <button className="ex-close" onClick={onClose}>close ×</button>
      <header className="ex-head">
        <div className="ex-deva">दीप</div>
        <div className="ex-title">Light a Diya</div>
        <p className="ex-sub">A small lamp, with a small wish. It will stay lit on this wall as long as you do not blow it out.</p>
      </header>
      <div className="ex-body">
        <div className="diya-form">
          <input className="ex-input" placeholder="your name (optional)" value={name} onChange={e => setName(e.target.value)} maxLength={48} />
          <input className="ex-input" placeholder="your wish, your intention, the name of someone you love" value={wish} onChange={e => setWish(e.target.value)} maxLength={140} />
          <button className="ex-cta" onClick={light} disabled={!wish.trim()}>light the diya</button>
        </div>
        <div className="diya-wall">
          {diyas.length === 0 && <p className="diya-empty">The wall is dark. Be the first to light one.</p>}
          {diyas.slice().reverse().map(d => (
            <div key={d.id} className="diya">
              <div className="flame" />
              <div className="oil" />
              <div className="diya-tip">
                <strong>{d.name}</strong>
                <span>"{d.wish}"</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ExtrasMount() {
  const which = useModal();
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closeModal(); };
    if (which) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [which]);
  if (!which) return null;
  if (which === "daana") return <Daana onClose={closeModal} />;
  if (which === "shop") return <GranthBhandar onClose={closeModal} />;
  if (which === "diya") return <DiyaWall onClose={closeModal} />;
  return null;
}

export function DaanaToggle() {
  return <button className="om-toggle" onClick={() => openModal("daana")}><span className="om-glyph" style={{ fontSize: 18 }}>दा</span><span className="om-label">daana</span></button>;
}

export function ShopToggle() {
  return <button className="om-toggle" onClick={() => openModal("shop")}><span className="om-glyph" style={{ fontSize: 18 }}>ग्रं</span><span className="om-label">bhandar</span></button>;
}

export function DiyaToggle() {
  return <button className="om-toggle" onClick={() => openModal("diya")}><span className="om-glyph" style={{ fontSize: 18 }}>दी</span><span className="om-label">diya</span></button>;
}